/**
 * ProposalsStage — stage 2 of the decision page.
 *
 * One row per option: who owns it, whether their proposal is in, when they
 * were asked, and the three things the decider can do about it — remind
 * them, copy their private link, or enter the proposal for them.
 */

import { useState } from 'react';
import { CheckIcon, ClipboardDocumentIcon } from '@heroicons/react/24/outline';
import { markOptionsInvited, sendBriefInvites } from '../../../services/brief/briefService';
import type { Brief, BriefOptionWithProposal } from '../../../types/brief';
import { btnSecondary } from '@/components/mono/primitives';

function relative(iso: string | null): string | null {
  if (!iso) return null;
  const diff = Date.now() - new Date(iso).getTime();
  const days = Math.floor(diff / 86_400_000);
  if (days <= 0) return 'today';
  if (days === 1) return 'yesterday';
  return `${days} days ago`;
}

function CopyLink({ url }: { url: string }) {
  const [state, setState] = useState<'idle' | 'copied' | 'failed'>('idle');
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setState('copied');
    } catch {
      setState('failed');
    }
    setTimeout(() => setState('idle'), 2500);
  };
  return (
    <button type="button" onClick={copy} {...btnSecondary()}>
      {state === 'copied' ? <CheckIcon className="w-3.5 h-3.5 text-[var(--green)]" /> : <ClipboardDocumentIcon className="w-3.5 h-3.5" />}
      {state === 'copied' ? 'Copied' : state === 'failed' ? 'Select and copy: ' + url : 'Copy link'}
    </button>
  );
}

export function ProposalsStage({
  brief,
  options,
  onOptionsChange,
  onError,
}: {
  brief: Brief;
  options: BriefOptionWithProposal[];
  onOptionsChange: (options: BriefOptionWithProposal[]) => void;
  onError: (message: string) => void;
}) {
  const base = window.location.origin;
  const collecting = brief.status === 'collecting';
  const [notice, setNotice] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);

  const remind = async (o: BriefOptionWithProposal) => {
    setBusyId(o.id);
    setNotice(null);
    try {
      const result = await sendBriefInvites(brief.id, [o.id], 'remind');
      if (result.sent > 0) {
        await markOptionsInvited([o.id], 'remind');
        const now = new Date().toISOString();
        onOptionsChange(options.map((x) => (x.id === o.id ? { ...x, last_reminded_at: now, invited_at: x.invited_at ?? now } : x)));
        setNotice(`Reminder sent to ${o.owner_name || o.owner_email}.`);
      } else {
        setNotice(result.skipped[0]?.reason ?? 'The reminder was not sent. Copy the link and send it yourself.');
      }
    } catch (err) {
      onError(err instanceof Error ? err.message : 'Could not send the reminder.');
    } finally {
      setBusyId(null);
    }
  };

  const named = options.filter((o) => o.name.trim());
  const submitted = named.filter((o) => o.proposal).length;

  return (
    <div className="space-y-4">
      <p className="text-sm text-[var(--text-sec)]">
        {brief.status === 'draft'
          ? 'Owners are asked for their proposals when you open this decision.'
          : `${submitted} of ${named.length} proposals in. Each owner writes theirs from a private link — no account needed.`}
      </p>

      {notice && (
        <p role="status" className="text-sm text-[var(--text-pri)] border border-[var(--rule)] bg-[var(--surface)] px-3 py-2">{notice}</p>
      )}

      <ul className="space-y-2">
        {named.map((o) => {
          const url = `${base}/brief/submit/${o.submit_token}`;
          const has = o.proposal !== null;
          const asked = relative(o.last_reminded_at ?? o.invited_at);
          return (
            <li key={o.id} className="flex flex-col sm:flex-row sm:items-center gap-3 border border-[var(--rule)] bg-[var(--surface)] px-4 py-3">
              <span className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${has ? 'bg-[var(--green)]' : 'bg-[var(--text-dim)]'}`} aria-hidden="true" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-[var(--text-pri)] truncate">{o.name}</p>
                <p className="text-xs text-[var(--text-ter)]">
                  {o.owner_name || o.owner_email || 'No owner named'}
                  {has
                    ? ` · submitted ${relative(o.proposal?.submitted_at ?? null) ?? ''}`
                    : asked
                      ? ` · asked ${asked}`
                      : o.owner_email
                        ? ' · not yet asked'
                        : ' · no email, so no link was sent'}
                </p>
              </div>
              {collecting && (
                <div className="flex items-center gap-2 flex-wrap">
                  {!has && o.owner_email && (
                    <button
                      type="button"
                      disabled={busyId === o.id}
                      onClick={() => remind(o)}
                      {...btnSecondary()}
                    >
                      {busyId === o.id ? 'Sending…' : 'Remind'}
                    </button>
                  )}
                  <CopyLink url={url} />
                  <a
                    href={url}
                    target="_blank"
                    rel="noreferrer"
                    {...btnSecondary()}
                  >
                    {has ? 'Edit it' : 'Enter it for them'}
                  </a>
                </div>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}
